<template>
	<v-container>
		<p>{{ description }}</p>
		<p style="font-weight: bold;">
			Source:
			<a :href="sourceUrl" target="_blank" rel="noopener noreferrer">
				{{ source }}
			</a>
		</p>
		<v-card class="pa-4">
			<div
				class="nis-cyber-world-map"
				@mouseleave="scheduleClearHover"
			>
				<svg
					class="world-map-svg"
					:viewBox="world.viewBox"
					role="img"
					:aria-label="title"
				>
					<path
						v-for="location in world.locations"
						:key="location.id"
						:d="location.path"
						class="world-map-region"
						:class="{
							'world-map-region--active': hasScore(location.id),
							'world-map-region--hovered': hoveredId === location.id,
						}"
						:style="{ fill: getFill(location.id) }"
						@mouseenter="setHover(location.id, $event)"
					/>
				</svg>

				<div
					v-if="hovered"
					class="map-tooltip"
					:style="tooltipStyle"
					@mouseenter="cancelClearHover"
					@mouseleave="scheduleClearHover"
				>
					<p class="map-tooltip-title mb-1">{{ hovered.name }}</p>
					<p class="map-tooltip-score mb-0">
						NCSI score:
						<strong>{{ hovered.score.toFixed(2) }}</strong>
					</p>
					<p class="map-tooltip-note mb-0 mt-1">
						{{ focusLabel }}
					</p>
				</div>
			</div>

			<div class="map-legend mt-4">
				<span class="legend-label">Lower readiness</span>
				<div class="legend-bar" />
				<span class="legend-label">Higher readiness</span>
			</div>
		</v-card>
	</v-container>
</template>

<script>
import world from "@svg-maps/world"
import ncsiData from "../data/ncsiCountryScores.json"

function scoreColor(score) {
	// Blue scale: low = light, high = dark (national cyber readiness)
	const t = Math.max(0, Math.min(1, Number(score) / 100))
	const r = Math.round(232 - t * 180)
	const g = Math.round(240 - t * 120)
	const b = Math.round(250 - t * 40)
	return `rgb(${r}, ${g}, ${b})`
}

export default {
	name: "NisWorldMapPanel",
	props: {
		title: { type: String, required: true },
		description: { type: String, required: true },
		focusLabel: {
			type: String,
			default: "National cyber readiness (NCSI)",
		},
	},
	data() {
		return {
			world,
			scores: ncsiData.scores || {},
			source: ncsiData.source,
			sourceUrl: ncsiData.sourceUrl,
			hoveredId: null,
			tooltipX: 0,
			tooltipY: 0,
			hideTooltipTimer: null,
		}
	},
	computed: {
		hovered() {
			if (!this.hoveredId) return null
			return this.scores[this.hoveredId] || null
		},
		tooltipStyle() {
			return {
				left: `${this.tooltipX}px`,
				top: `${this.tooltipY}px`,
			}
		},
	},
	beforeDestroy() {
		this.cancelClearHover()
	},
	methods: {
		hasScore(id) {
			return Boolean(this.scores[id])
		},
		getFill(id) {
			const entry = this.scores[id]
			if (!entry) return "#ececec"
			return scoreColor(entry.score)
		},
		setHover(id, event) {
			if (!this.scores[id]) return
			this.cancelClearHover()
			this.hoveredId = id
			if (event && this.$el) {
				const mapRoot = this.$el.querySelector(".nis-cyber-world-map")
				const bounds = (mapRoot || this.$el).getBoundingClientRect()
				this.tooltipX = event.clientX - bounds.left + 12
				this.tooltipY = event.clientY - bounds.top + 12
			}
		},
		scheduleClearHover() {
			this.cancelClearHover()
			this.hideTooltipTimer = window.setTimeout(() => {
				this.hoveredId = null
				this.hideTooltipTimer = null
			}, 250)
		},
		cancelClearHover() {
			if (this.hideTooltipTimer) {
				window.clearTimeout(this.hideTooltipTimer)
				this.hideTooltipTimer = null
			}
		},
	},
}
</script>

<style scoped>
.nis-cyber-world-map {
	position: relative;
	width: 100%;
	min-height: 420px;
}

.world-map-svg {
	display: block;
	width: 100%;
	height: auto;
}

.world-map-region {
	stroke: #ffffff;
	stroke-width: 0.5;
	transition: fill 0.15s ease, opacity 0.15s ease;
}

.world-map-region--active {
	cursor: pointer;
}

.world-map-region--active:hover,
.world-map-region--hovered {
	opacity: 0.9;
	filter: brightness(0.92);
}

.map-tooltip {
	position: absolute;
	z-index: 2;
	max-width: 260px;
	padding: 12px 14px;
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 8px;
	background: #ffffff;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
	pointer-events: auto;
}

.map-tooltip-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1a1a1a;
}

.map-tooltip-score {
	font-size: 0.875rem;
	color: #424242;
}

.map-tooltip-note {
	font-size: 0.75rem;
	color: #757575;
	line-height: 1.35;
}

.map-legend {
	display: flex;
	align-items: center;
	gap: 10px;
	max-width: 420px;
}

.legend-label {
	font-size: 0.75rem;
	color: #616161;
	white-space: nowrap;
}

.legend-bar {
	flex: 1;
	height: 10px;
	border-radius: 4px;
	background: linear-gradient(
		90deg,
		rgb(232, 240, 250) 0%,
		rgb(52, 120, 210) 100%
	);
}
</style>
